import React, { useEffect, useMemo, useRef } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./DashboardBannerSlider.css";

import PrevIcon from "../assets/images/previous.png";
import NextIcon from "../assets/images/next.png";
import { useAdverts } from "../hooks/useAdverts";
import { getStoredUser } from "../utils/authStorage";
import { storeAdvertInteraction } from "../services/advertService";

const EXAMS_BANNER_CATEGORY_ID = 2;
const EXAMS_BANNER_POSITION = "exams_banner";

const PrevArrow = ({ className, onClick }) => (
  <button type="button" className={`${className} arrow prev`} onClick={onClick}>
    <img src={PrevIcon} alt="Previous banner" />
  </button>
);

const NextArrow = ({ className, onClick }) => (
  <button type="button" className={`${className} arrow next`} onClick={onClick}>
    <img src={NextIcon} alt="Next banner" />
  </button>
);

export default function DashboardBannerSlider() {
  const storedUser = getStoredUser();
  const stateId = Number(
    storedUser?.state_id || storedUser?.stateId || storedUser?.user_state_id || 0
  );
  const trackedViewsRef = useRef(new Set());

  const { data: advertsData } = useAdverts({
    states_id: stateId,
    category_id: EXAMS_BANNER_CATEGORY_ID,
    ad_format: "regular",
  });

  const banners = useMemo(() => {
    const advertItems = Array.isArray(advertsData?.dataset?.data) ? advertsData.dataset.data : [];
    return advertItems
      .map((item, index) => ({
        id: Number(item?.id || index + 1),
        image: item?.image_url || item?.advert_image || item?.image || "",
        url: item?.advert_url || item?.url || "",
        name: item?.name || item?.title || `Advert ${index + 1}`,
      }))
      .filter((item) => item.image);
  }, [advertsData]);

  useEffect(() => {
    banners.forEach((item) => {
      if (!item?.id || trackedViewsRef.current.has(item.id)) {
        return;
      }

      trackedViewsRef.current.add(item.id);
      storeAdvertInteraction({
        position: EXAMS_BANNER_POSITION,
        adverts_id: item.id,
        interaction_type: 0,
        web_url: window.location.href,
        states_id: stateId,
        category_id: EXAMS_BANNER_CATEGORY_ID,
      }).catch(() => {});
    });
  }, [banners, stateId]);

  const handleBannerClick = (banner) => {
    if (!banner?.url) {
      return;
    }

    storeAdvertInteraction({
      position: EXAMS_BANNER_POSITION,
      adverts_id: banner.id,
      interaction_type: 1,
      web_url: window.location.href,
      states_id: stateId,
      category_id: EXAMS_BANNER_CATEGORY_ID,
    }).catch(() => {});
    window.open(banner.url, "_blank", "noopener,noreferrer");
  };

  return <AdvertBannerSlider banners={banners} onBannerClick={handleBannerClick} />;
}

// Reuse the existing banner presentation with ads fetched by other modules.
export function AdvertBannerSlider({ banners = [], onBannerClick }) {
  if (!banners.length) {
    return null;
  }

  const settings = {
    infinite: banners.length > 1,
    speed: 500,
    slidesToShow: 1,
    centerMode: banners.length > 1,
    centerPadding: banners.length > 1 ? "20%" : "0px",
    arrows: banners.length > 1,
    dots: banners.length > 1,
    swipeToSlide: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    responsive: [
      {
        breakpoint: 767,
        settings: {
          swipe: true,
          touchMove: true,
          centerMode: banners.length > 1,
          centerPadding: banners.length > 1 ? "10%" : "0px",
        },
      },
    ],
  };

  return (
    <div className="BannerDashboard">
      <Slider {...settings}>
        {banners.map((banner, index) => (
          <div key={`${banner.id}-${index}`} className="slide">
            <button
              type="button"
              className="BannerDashboard__slideButton"
              onClick={() => onBannerClick?.(banner)}
              disabled={!banner.url}
            >
              <img src={banner.image} alt={banner.name || `slide-${index + 1}`} />
            </button>
          </div>
        ))}
      </Slider>
    </div>
  );
}
